import { useCallback } from "react";
import type { KeyboardEvent } from "react";

/**
 * W-16 — roving tabindex + arrow-key selection for radio-style button groups
 * (goal grid, plan-length cards, method cards, sweat, gut). ArrowRight/Down
 * selects + focuses the next SELECTABLE item, ArrowLeft/Up the previous,
 * wrapping at the ends and skipping any item `isSelectable` rules out. Only
 * the selected item (or the first, when nothing is selected) is tabbable, so
 * Tab enters the group once and arrows move within it.
 *
 * Arrow-selection SELECTS only — it never auto-advances a step (that stays a
 * click/tap affordance on the one-decision screens).
 *
 * Focus is moved via the shared parent's children, so the items must be the
 * direct children of one container (the `role="radiogroup"` element).
 */
export function useRovingRadio({
  length,
  selectedIndex,
  onArrowSelect,
  isSelectable
}: {
  /** Item count in the group. */
  length: number;
  /** Index of the selected item, or -1 when nothing is selected yet. */
  selectedIndex: number;
  /** Select the item at this index (same state change as a click, no advance). */
  onArrowSelect: (index: number) => void;
  /** Which items arrow-selection may land on (default: all). Non-selectable
   *  items (e.g. the Method step's import cards, which launch flows instead of
   *  selecting) are skipped entirely — arrows jump past them. */
  isSelectable?: (index: number) => boolean;
}) {
  const tabIndexFor = useCallback(
    (index: number): number =>
      selectedIndex === -1 ? (index === 0 ? 0 : -1) : selectedIndex === index ? 0 : -1,
    [selectedIndex]
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>, index: number) => {
      let dir = 0;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") dir = 1;
      else if (e.key === "ArrowLeft" || e.key === "ArrowUp") dir = -1;
      if (dir === 0) return;
      e.preventDefault();
      // Advance to the NEXT selectable index (wrapping, skipping non-selectable
      // items). At most `length` probes, so a group with nothing selectable
      // can't loop forever — the keypress is just a no-op.
      let next: number | null = null;
      for (let offset = 1; offset <= length; offset++) {
        const candidate = (((index + dir * offset) % length) + length) % length;
        if (!isSelectable || isSelectable(candidate)) {
          next = candidate;
          break;
        }
      }
      if (next == null) return;
      const target = next;
      const parent = e.currentTarget.parentElement;
      onArrowSelect(target);
      // Focus after the selection re-render so the roving tabindex has settled.
      requestAnimationFrame(() => {
        const el = parent?.children[target] as HTMLElement | undefined;
        el?.focus?.();
      });
    },
    [length, onArrowSelect, isSelectable]
  );

  return { tabIndexFor, onKeyDown };
}
